import { RewardControllerMembers200Response, UtilLevelControllerGet200Response } from "@api/api";
import { GetLevelModel } from "@factory/model/query-model-level";
import { RewardMemberModel } from "@factory/model/query-model-reward";
import { useQyGetLevel } from "@factory/query/level.query";
import { useQyGetMemberReward } from "@factory/query/reward.query";
import { TableSort } from "@shared/models/table-sort";
import ErrorSection from "@shared/ui/error-section/error-section";
import HeaderContent from "@shared/ui/header-content/header-content";
import SearchInput from "@shared/ui/search-input/search-input";
import SkeletonList from "@shared/ui/skeleton-list/skeleton-list";
import { useDateTimeContext } from "@shared/utility/date-time-service/date-time.context";
import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { DataTable, DataTableStateEvent } from "primereact/datatable";
import { Paginator, PaginatorPageChangeEvent } from "primereact/paginator";
import { Sidebar } from "primereact/sidebar";
import { useState } from "react";
import { CiSearch } from "react-icons/ci";
import { MdClear } from "react-icons/md";
import { useNavigate } from "react-router-dom";

import { appFeature } from "../../../app.feature";
import { FilterPerson } from "../filter-person/filter-person";
import { useFilterPersonContext } from "../filter-person/filter-person.context";

export function ListCustomer() {
  // THIS IS THE LOCAL DECLARATION
  const { pointsText, stampsText } = appFeature.config.label;
  const { formatDateLocal } = useDateTimeContext();
  const navigate = useNavigate();
  const [rowLimit, setRowLimit] = useState(20);
  const [pageNumber, setPageNumber] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [first, setFirst] = useState(0);
  const [tableSort, setTableSort] = useState<TableSort>({
    sortField: undefined,
    sortOrder: undefined,
  });
  const [sort, setSort] = useState({});
  const [filterPanel, setFilterPanel] = useState(false);
  const { filterChipsElement } = useFilterPersonContext();
  const [selectedRank, setSelectedRank] = useState<string | undefined>("");
  const [levels, setLevels] = useState<GetLevelModel[] | undefined>([]);
  const [levelCount, setLevelCount] = useState<Record<string, number> | undefined>();
  const [levelFilter, setLevelFilter] = useState<Record<string, string> | undefined>();

  // THIS IS THE QUERY DECLARATION
  const {
    data: personResponse,
    isLoading,
    isError,
    error,
  } = useQyGetMemberReward(searchTerm, rowLimit, pageNumber, sort, levelFilter);

  const handleGetLevelSuccess = (data: UtilLevelControllerGet200Response) => {
    if (!data?.data) return;
    setLevels(data.data);
  };
  const { data: utilLevelResponse } = useQyGetLevel(
    "",
    999999,
    0,
    {order: JSON.stringify({points: 'ASC'})},
    undefined,
    true,
    handleGetLevelSuccess,
  );
  const getLevelCounts = (members: any[]): Record<string, number> => {
    return members.reduce((acc, member) => {
      // Fallback to 'No Level' if the property is missing or null
      const levelName = member.level_name || 'No Level';
      
      // Increment the count for this level name
      acc[levelName] = (acc[levelName] || 0) + 1;
      
      return acc;
    }, {} as Record<string, number>);
  };
  const handleCountSuccess = (data: RewardControllerMembers200Response) => {
    if (!data?.data) return;
    const count = getLevelCounts(data.data)
    setLevelCount(count) 
  }
  useQyGetMemberReward("", 99999, 0, undefined, undefined, true, handleCountSuccess);

  // THIS IS THE LOCAL DECLARATION
  const handleSearch = (searchTerm: string) => {
    setSearchTerm(searchTerm);
  };
  const handleTableSort = (e: DataTableStateEvent) => {
    setTableSort({
      sortField: e.sortField,
      sortOrder: e.sortOrder,
    });
    const order = e.sortOrder === 1 ? "ASC" : "DESC";
    setSort({ [e.sortField]: order });
  };
  const navigateToRecord = (item: RewardMemberModel) => {
    navigate(`${item.user_code}`);
  };
  const onPageChange = (event: PaginatorPageChangeEvent) => {
    const offsetValue = event.page * rowLimit;
    setFirst(event.first);
    setPageNumber(offsetValue);
    setRowLimit(event.rows);
  };
  const handleNew = () => {
    navigate(`new`);
  };

  // THIS IS THE LOCAL RENDERS
  const displayLoading = (
    <div className="card">
      <SkeletonList count={4} />
    </div>
  );
  const displayError = (
    <div className="card">
      <ErrorSection title="Error Occured" message={(error as any)?.message} />
    </div>
  );
  const filterElement = (
    <section>
      <div className="flex gap-4 items-center mb-2">
        <SearchInput
          clearIcon={<MdClear />}
          searchIcon={<CiSearch />}
          searchTerm={searchTerm}
          onSearch={handleSearch}
          placeholder="Search items"
          className="w-full block"
        />
        <Button
          label="Filter"
          severity="secondary"
          badgeClassName="p-badge-danger"
          onClick={() => setFilterPanel(true)}
          outlined
        />
      </div>

      {filterChipsElement}
      <Sidebar visible={filterPanel} onHide={() => setFilterPanel(false)}>
        <FilterPerson />
      </Sidebar>
    </section>
  );
  const grid = (
    <section className="mt-6">
      <section className="flex space-x-2 mb-2">
        {levels?.map((item, index) => (
          <Button
            label={`${item.name}:${levelCount?.[item.name || ""] || 0}`}
            className={`rounded-full`}
            outlined={selectedRank !== item.code}
            onClick={() => {
              setSelectedRank(item.code);
              setLevelFilter({
                level: item.code,
              })
            }}
          />
        ))}
      </section>
      <h5 className="mb-2">
        Total: <b>{personResponse?.count}</b>
      </h5>

      <DataTable
        value={personResponse?.data}
        selectionMode="single"
        onSelectionChange={(e) => navigateToRecord(e.value)}
        onSort={handleTableSort}
        sortField={tableSort.sortField}
        sortOrder={tableSort.sortOrder}
      >
        <Column field="first_name" header="First Name" sortable></Column>
        <Column field="last_name" header="Last Name" sortable></Column>
        <Column field="username" header="Username" sortable></Column>
        <Column field="email" header="Email"></Column>
        <Column field="mobile_number" header="Mobile Number"></Column>
        <Column
          field="level_sort"
          header="Total Level"
          sortable
          body={(item) => item.level_name}
        ></Column>
        <Column
          field="current_points"
          header={`Available ${pointsText}`}
        ></Column>
        <Column
          field="current_stamp"
          header={`Available ${stampsText}`}
        ></Column>
        <Column
          field="total_redeem_points"
          header={`Redeemed ${pointsText}`}
        ></Column>
        <Column
          field="total_redeem_stamp"
          header={`Redeemed ${stampsText}`}
        ></Column>
        <Column
          body={(cell) =>
            cell?.created_at ? formatDateLocal(cell?.created_at) : "-"
          }
          field="created_at"
          header="Registered"
        ></Column>
      </DataTable>

      <Paginator
        first={first}
        rows={rowLimit}
        totalRecords={personResponse?.count}
        rowsPerPageOptions={[10, 20, 30]}
        onPageChange={onPageChange}
      />
    </section>
  );

  return (
    <div id="ListPerson" className="person">
      <HeaderContent title={<h1>Customer</h1>}>
        <span className="flex gap-2">
          <Button label="New" onClick={handleNew} />
        </span>
      </HeaderContent>

      <div className="p-7">
        {filterElement}
        {isLoading && displayLoading}
        {isError && !isLoading && displayError}
        {!isLoading && !isError && grid}
      </div>
    </div>
  );
}

export default ListCustomer;
